import React, { useContext, useMemo } from 'react';
import './SymbolTile.scss';
import useWidgetStream from 'widgets/streaming/useWidgetStream';
import { TileToolbar } from 'widgets/tiles/components/tile/TileToolbar';
import { SymbolFromConfigOrPortfolioLinkedStatus } from 'portfolio/components/SymbolFromConfigOrPortfolioLinkedStatus';
import SymbolTileSettings, {
    SymbolTileModalClassName,
    SymbolTileSettingsText,
} from 'widgets/symbolTile/components/settings/SymbolTileSettings';

import { useSymbolTileConfigSlice } from 'widgets/symbolTile/store/instances/config/configslice';
import { SymbolTileContent } from 'widgets/symbolTile/components/SymbolTileContent';
import {
    buildSymbolTileData,
    useSymbolTileMessages,
} from 'widgets/symbolTile/components/symbolTileData';
import SymbolTileChartHelp from 'widgets/symbolTile/components/chart/help/SymbolTileChartHelp';
import { WidgetTile } from 'widgets/tiles/components/tile/WidgetTile';
import classNames from 'classnames';
import { useSymbolTileStreamData } from 'widgets/symbolTile/store/data/hooks';
import ConfigSettingsModal from 'widgets/tiles/components/tile/ConfigSettingsModal';
import { useCapsuleBoundSymbol } from 'widgets/tiles/components/tile/hooks';
import AppContext from 'app/AppContext';
import {
    useSymbolExternalMarketData,
    useSymbolExternalMarketDataLatestTick,
} from 'marketData/hooks';
import { SymbolTileMarketDataBarSize } from 'widgets/symbolTile/stream/SymbolTilesStreamManager';
import { useSymbolInUsersSymbology } from 'symbols/symbolInfo/hooks';

export function SymbolTile() {
    const { config, dispatchConfigChanged } = useSymbolTileConfigSlice();
    const { showDataFor } = config;
    const { symbol, symbology } = useCapsuleBoundSymbol();
    const symbolInUsersSymbology = useSymbolInUsersSymbology(symbol, symbology);
    const streamData = useSymbolTileStreamData(symbolInUsersSymbology);
    const { numberFormat } = useContext(AppContext);
    const { barData, messages: externalMarketDataMessages } =
        useSymbolExternalMarketData(
            symbol,
            symbology,
            SymbolTileMarketDataBarSize
        );
    const externalMarketDataLatestTick = useSymbolExternalMarketDataLatestTick(
        symbol,
        symbology
    );

    const data = buildSymbolTileData(
        numberFormat,
        streamData,
        barData,
        externalMarketDataLatestTick,
        showDataFor
    );

    useWidgetStream();

    const widgetMessages = useSymbolTileMessages();
    const messages = [...externalMarketDataMessages, ...widgetMessages];

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const warning = useMemo(() => <SymbolWarning />, [config]);

    const cssClasses = classNames('SymbolTile');

    return (
        <WidgetTile className={cssClasses}>
            <TileToolbar
                settingsBody={<SymbolTileSettings />}
                settingsHeader={SymbolTileSettingsText}
                settingsClassName={SymbolTileModalClassName}
                messages={messages}
                title={'Price'}
                preceedingItems={<SymbolTileChartHelp />}
            />

            <div className="capsule-body">
                <SymbolFromConfigOrPortfolioLinkedStatus warning={warning}>
                    <SymbolTileContent
                        data={data}
                        config={config}
                        dispatchConfigChanged={dispatchConfigChanged}
                    />
                </SymbolFromConfigOrPortfolioLinkedStatus>
            </div>
        </WidgetTile>
    );

    function SymbolWarning() {
        return (
            <span>
                Select a symbol or specify one in the&nbsp;
                <ConfigSettingsModal
                    dispatcher={dispatchConfigChanged}
                    triggerText={'Capsule Settings'}
                    headerText={SymbolTileSettingsText}
                    className={SymbolTileModalClassName}
                    renderSettings={(p) => <SymbolTileSettings {...p} />}
                />
                .
            </span>
        );
    }
}
